import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { PlusIcon } from '@repo/ui/icons'
import MultiSelectInput from '@repo/ui/multiselect-input'
import ScrollableDiv from '@repo/ui/scrollable-div'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getHobbies } from '../../../../api/hobbies/get-hobbies'
import useTranslate from '../../../../lib/hooks/use-translate'
import { DocumentStatusEnum } from '../../../../lib/types/enums/common/document-status.enum'
import { HobbyAudienceEnum } from '../../../../lib/types/enums/hobby/hobby-audience.enum'
import {
  LanguageEnum,
  languageEnumMapping
} from '../../../../lib/types/enums/languages/language.enum'
import {
  LanguageLevelEnum,
  languageLevelEnumMapping
} from '../../../../lib/types/enums/languages/language-level.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import {
  DiplomaEnum,
  diplomaEnumMapping
} from '../../../../lib/types/enums/staff-application/diploma.enum'
import {
  EducationLevelEnum,
  educationLevelEnumMapping
} from '../../../../lib/types/enums/staff-application/education-level.enum'
import {
  SchoolLevelTaughtEnum,
  schoolLevelTaughtEnumMapping
} from '../../../../lib/types/enums/staff-application/school-level-taught.enum'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import { ControlledAdressInput } from '../../../elements/inputs/address-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import DocumentCard from '../document-card'
import {
  diplomasThatRequireFiles,
  diplomasWithPaySlip
} from '../utils/diplomas-required-documents'

const StaffProfessionalInformationForm = () => {
  const { t } = useTranslate()
  const form = useFormContext<AdminStaffInput>()
  const languageMapping = languageEnumMapping()

  const { data: hobbies } = useQuery({
    queryKey: ['hobbies', { automaticallyGenerated: false, visibleTo: [HobbyAudienceEnum.STAFF] }],
    queryFn: () =>
      getHobbies({
        filter: { automaticallyGenerated: false, visibleTo: [HobbyAudienceEnum.STAFF] }
      })
  })

  const diplomas = form.watch('diplomas')
  const paySlipStatus = form.watch('paySlipStatus')
  const position = form.watch('position')
  const isAnimator = position === StaffPositionEnum.ANIMATOR

  const handlePositionChange = (
    position: StaffPositionEnum,
    onChange: (position: StaffPositionEnum) => void
  ) => {
    onChange(position)

    if (position === StaffPositionEnum.ANIMATOR) {
      form.setValue('paySlip', undefined, { shouldValidate: true })
      form.setValue('paySlipStatus', DocumentStatusEnum.PENDING)
      form.setValue(
        'diplomas',
        form.getValues('diplomas').filter((diploma) => diploma.type !== DiplomaEnum.CAPES)
      )
      form.clearErrors('paySlip')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Poste: *
      </Typography>
      <div className="flex gap-2">
        {Object.values(StaffPositionEnum).map((position) => (
          <FormField
            key={position}
            control={form.control}
            name="position"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <ControlledRadioInput
                      {...field}
                      label={t(staffPositionEnumMapping[position])}
                      checked={field.value === position}
                      onCheck={() => handlePositionChange(position, field.onChange)}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        ))}
      </div>
      <div className="flex gap-3 items-center w-full">
        <FormField
          control={form.control}
          name="qualifications"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledMultiSelectInput
                  {...field}
                  required
                  searchable
                  variant="primary"
                  label="Qualifications"
                  options={Object.values(StaffQualificationEnum).map((qualification) => ({
                    label: t(staffQualificationEnumMapping[qualification].label),
                    value: qualification
                  }))}
                  selectedOptions={field.value?.map((qualification) => ({
                    label: t(staffQualificationEnumMapping[qualification].label),
                    value: qualification
                  }))}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="currentSituation"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Situation actuelle" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="w-full flex items-center gap-3">
        <FormField
          control={form.control}
          name="studiedDomain"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Domaine d'étude" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="educationLevel"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  label="Niveau d'études"
                  options={Object.values(EducationLevelEnum).map((level) => ({
                    label: educationLevelEnumMapping[level],
                    value: level
                  }))}
                  selectedOption={getSelectedEnumOption(field.value, educationLevelEnumMapping)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="w-full flex items-center gap-3">
        <FormField
          control={form.control}
          name="classLevels"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <MultiSelectInput
                  {...field}
                  label="Niveaux enseignés"
                  variant="primary"
                  searchable
                  options={Object.values(SchoolLevelTaughtEnum).map((level) => ({
                    label: t(schoolLevelTaughtEnumMapping[level]),
                    value: level
                  }))}
                  selectedOptions={field.value?.map((level) => ({
                    label: t(schoolLevelTaughtEnumMapping[level]),
                    value: level
                  }))}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="hobbyIds"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <MultiSelectInput
                  {...field}
                  label="Loisirs"
                  variant="primary"
                  searchable
                  options={hobbies?.data?.map((hobby) => ({
                    label: hobby.name,
                    value: hobby.id
                  }))}
                  selectedOptions={field.value?.map((id) => ({
                    label: hobbies?.data?.find((hobby) => hobby.id === id)?.name ?? '',
                    value: id
                  }))}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Établissements
      </Typography>
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name="establishmentName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="NOM de l'établissement" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="establishmentAddress"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledAdressInput
                  {...field}
                  label="Adresse"
                  onDetailsChange={(placeDetails) => {
                    form.setValue('establishmentAddress', placeDetails.address, {
                      shouldValidate: true
                    })
                    form.setValue('establishmentPostalCode', placeDetails.postalCode, {
                      shouldValidate: true
                    })
                    form.setValue('establishmentCity', placeDetails.city, { shouldValidate: true })
                    form.trigger([
                      'establishmentAddress',
                      'establishmentPostalCode',
                      'establishmentCity'
                    ])
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex gap-2">
        <FormField
          control={form.control}
          name="establishmentPostalCode"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Code postal de l'établissement" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="establishmentCity"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <ControlledInput {...field} label="Ville de l'établissement" type="text" />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Langues pratiquées
      </Typography>
      {Object.values(LanguageEnum).map((language, index) => {
        const Icon = languageMapping[language].icon
        return (
          <div key={`${language}-${index}`} className="mb-3">
            <div className="flex items-center gap-1">
              <Typography
                variant={'label-semibold'}
                className={'flex gap-1 items-center'}
                color={tailwindColors.grey.lighter}
              >
                {languageMapping[language].label}
                <Icon className={'min-w-4 min-h-4 w-4 h-4 rounded-full'} />
              </Typography>
            </div>
            <div className="flex items-center mt-1 gap-6">
              {Object.values(LanguageLevelEnum).map((level) => (
                <FormField
                  key={`${language}-${level}`}
                  control={form.control}
                  name={`languages.${index}.level`}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={languageLevelEnumMapping[level].label}
                            checked={field.value === level}
                            onCheck={() => {
                              form.setValue(`languages.${index}.language`, language)
                              field.onChange(level)
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )
                  }}
                />
              ))}
            </div>
          </div>
        )
      })}
      <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
        Diplômes :
      </Typography>
      <div className="flex items-center gap-6 flex-wrap">
        {Object.values(DiplomaEnum).map((diplomaEnumValue) =>
          isAnimator && diplomaEnumValue === DiplomaEnum.CAPES ? null : diplomaEnumValue !==
            DiplomaEnum.OTHER ? (
            <Checkbox
              label={t(diplomaEnumMapping[diplomaEnumValue])}
              key={diplomaEnumValue}
              disabled={
                // Disable if THIS diploma is verified, or if THIS diploma is controlled by pay slip and the pay slip is verified
                diplomas?.find((diploma) => diploma.type === diplomaEnumValue)?.status ===
                  DocumentStatusEnum.VERIFIED ||
                (diplomasWithPaySlip.includes(diplomaEnumValue) &&
                  paySlipStatus === DocumentStatusEnum.VERIFIED)
              }
              checked={diplomas.map((diploma) => diploma.type).includes(diplomaEnumValue)}
              onChange={(checked) => {
                if (checked)
                  form.setValue('diplomas', [
                    ...(form.getValues('diplomas') ?? []),
                    { type: diplomaEnumValue, status: DocumentStatusEnum.PENDING }
                  ])
                else
                  form.setValue(
                    'diplomas',
                    diplomas.filter((diploma) => diploma.type !== diplomaEnumValue)
                  )
              }}
            />
          ) : (
            <Button
              type="button"
              key={diplomaEnumValue}
              variant="ghost"
              className="px-4 py-3"
              icon={PlusIcon}
              label={t(diplomaEnumMapping[diplomaEnumValue])}
              onClick={() => {
                form.setValue('diplomas', [
                  ...(form.getValues('diplomas') ?? []),
                  {
                    type: DiplomaEnum.OTHER,
                    status: DocumentStatusEnum.PENDING
                  }
                ])
              }}
            />
          )
        )}
      </div>
      <ScrollableDiv className="flex gap-5 overflow-auto  py-5">
        {diplomas.map((diploma, index) => {
          const file = diplomas[index].file
          if (diplomasThatRequireFiles.includes(diploma.type))
            return (
              <FormField
                key={`${diploma.type}-${index}`}
                control={form.control}
                name={`diplomas.${index}.file`}
                render={() => (
                  <DocumentCard
                    className="w-72 flex-shrink-0"
                    disabled={form.formState.defaultValues?.diplomas?.some(
                      (d) => d?.type === diploma.type && d.status === DocumentStatusEnum.VERIFIED
                    )}
                    errorMessage={form.formState.errors.diplomas?.[index]?.file?.message}
                    title={t(diplomaEnumMapping[diploma.type])}
                    onFilesChange={(files) => {
                      form.setValue(`diplomas.${index}.file`, files[0], { shouldValidate: true })
                      form.setValue(
                        `diplomas.${index}.status`,
                        files[0] ? DocumentStatusEnum.RECEIVED : DocumentStatusEnum.PENDING
                      )
                    }}
                    files={file ? [file] : []}
                    status={diploma.status}
                    statusPosition="inside"
                    requireVerification
                    onStatusChange={(status) => {
                      form.setValue(`diplomas.${index}.status`, status)
                    }}
                    canRemove={diploma.type === DiplomaEnum.OTHER}
                    onRemoveCard={() => {
                      if (diploma.type === DiplomaEnum.OTHER)
                        form.setValue(
                          'diplomas',
                          diplomas.filter((_, i) => i !== index)
                        )
                    }}
                  >
                    {diploma.type === DiplomaEnum.OTHER ? (
                      <>
                        <FormField
                          control={form.control}
                          name={`diplomas.${index}.diplomaTitle`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <ControlledInput
                                  {...field}
                                  label="NOM du diplôme"
                                  type="text"
                                  disabled={diploma.status === DocumentStatusEnum.VERIFIED}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <div className="h-4" />
                      </>
                    ) : null}
                  </DocumentCard>
                )}
              />
            )
        })}
      </ScrollableDiv>
    </div>
  )
}

export default StaffProfessionalInformationForm
